/**
 * Expense claims — «Utlegg».  MKK module 23 (B2, 2026-10-24).
 *
 * A staff member or volunteer paid for something the mosque needed (food for an iftar, a cable, a bus ticket) and asks
 * for the money back: a title, the amount in øre, the day it was spent, a category, a few words, the bank account the
 * money goes to, and the receipts. Somebody ELSE approves it (four eyes: never the claimant), and somebody with the
 * bank marks it paid with the transfer's reference. Behind the `expenses` capability (privacy scope `expenses`: the
 * account number is personal data of staff). Three permissions (access.js):
 *   expenses.submit    my own claims: write, change while submitted, withdraw, add receipts
 *   expenses.approve   every claim: approve or reject, with a note
 *   expenses.pay       every claim: mark an approved claim paid, with a reference; the CSV for the accountant
 *
 * The claim moves: submitted → approved | rejected | withdrawn (withdrawn only by the claimant); approved → paid.
 * Rejected, paid and withdrawn are final. A claim is edited only while submitted, only by the claimant. Approval needs
 * at least one receipt. Every move is one row in `expense_event`, the trail bookkeeping needs (bokføringsloven keeps
 * paid and rejected claims five years after the accounting year; nothing here deletes).
 *
 * Pure: no I/O, no clock — "today" (mosque-local, tz.dateIn) is passed in.
 */
const Day = require('./day');
const { oneLine, text, isId } = require('./tasks');
const RF = require('./request-files');
const Csv = require('./csv');
const { formatKr, kr } = require('./money');

const STATUSES = ['submitted', 'approved', 'rejected', 'paid', 'withdrawn'];
const OPEN = ['submitted', 'approved'];
const FINAL = ['rejected', 'paid', 'withdrawn'];
const CATEGORIES = ['food', 'equipment', 'travel', 'event', 'maintenance', 'other'];
const STATUS_NO = { submitted: 'Sendt', approved: 'Godkjent', rejected: 'Avvist', paid: 'Betalt', withdrawn: 'Trukket' };
const CATEGORY_NO = { food: 'Mat og drikke', equipment: 'Utstyr', travel: 'Reise', event: 'Arrangement', maintenance: 'Vedlikehold', other: 'Annet' };
const MAX_TITLE = 120;
const MAX_DESCRIPTION = 1000;
const MAX_NOTE = 500;
const MAX_REF = 80;
/** Whole øre, more than nothing, at most 100 000 kr: a claim above that is not an utlegg but an invoice to the board. */
const MAX_AMOUNT_ORE = 100_000_00;
/** Receipts per claim, and what one may weigh (a phone photo of a receipt is 1–4 MB; a scanned PDF more). */
const MAX_RECEIPTS = 10;
const MAX_RECEIPT_BYTES = 8 * 1024 * 1024;
const MAX_CLAIMS = 20000;
/** The fields a claimant may send. */
const FIELDS = ['title', 'description', 'amountOre', 'spentOn', 'category', 'accountNo'];

/**
 * A Norwegian bank account number: eleven digits, the last a MOD11 control digit over the first ten (weights 5 4 3 2
 * 7 6 5 4 3 2). Spaces and dots as people write them («1234.56.78903») are dropped. → { ok, value } | { ok: false, message }
 */
function accountNumber(v) {
  if (v === undefined || v === null || v === '') return { ok: false, message: 'Skriv kontonummeret pengene skal til.' };
  if (typeof v !== 'string' && typeof v !== 'number') return { ok: false, message: 'Kontonummeret må være tekst.' };
  const s = String(v).replace(/[\s.]/g, '');
  if (!/^\d{11}$/.test(s)) return { ok: false, message: 'Kontonummeret har elleve sifre.' };
  if (!mod11(s)) return { ok: false, message: 'Kontonummeret stemmer ikke (kontrollsifferet er feil). Sjekk sifrene.' };
  return { ok: true, value: s };
}
function mod11(digits) {
  const w = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const sum = w.reduce((acc, weight, i) => acc + weight * Number(digits[i]), 0);
  let check = 11 - (sum % 11);
  if (check === 11) check = 0;
  if (check === 10) return false;
  return check === Number(digits[10]);
}
/** «1234 56 78903» — how a Norwegian reads one. */
const accountText = (s) => (/^\d{11}$/.test(String(s || '')) ? `${s.slice(0, 4)} ${s.slice(4, 6)} ${s.slice(6)}` : String(s || ''));
/** The same with the first seven digits hidden: for people who may act on a claim but need not see the account. */
const accountMasked = (s) => (/^\d{11}$/.test(String(s || '')) ? `•••• •• •${s.slice(7)}` : '');

/**
 * A sum as a person types it, in kroner: «149», «149,50», «1 250.00». Whole øre only (two decimals at most), more than
 * nothing. → { ok, ore } | { ok: false, message }
 */
function kroner(input) {
  const s = String(input ?? '').trim().replace(/[\s ]/g, '').replace(/kr$/i, '');
  const m = /^(\d{1,7})(?:[.,](\d{1,2}))?$/.exec(s);
  if (!m) return { ok: false, message: 'Skriv beløpet i kroner, for eksempel 149 eller 149,50.' };
  const ore = Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0') || 0);
  const r = amount(ore);
  return r.ok ? { ok: true, ore } : { ok: false, message: r.message };
}
/** Whole øre from 1 to MAX_AMOUNT_ORE — never a fraction, never a string of digits read as 0. */
function amount(v) {
  if (!Number.isSafeInteger(v) || v <= 0) return { ok: false, message: 'Beløpet må være et helt antall øre, mer enn 0.' };
  if (v > MAX_AMOUNT_ORE) return { ok: false, message: `Beløpet kan være høyst ${kr(MAX_AMOUNT_ORE)}.` };
  return { ok: true, value: v };
}

/**
 * A claim as the claimant sends it — every field checked, unknown fields refused. `base` is the claim as it is now for
 * a change, or nothing for a new one; `today` the mosque's day (a receipt from tomorrow is a typo).
 * → { ok, value: { title, description, amountOre, spentOn, category, accountNo }, changed: [field…] } | { ok: false, errors }
 */
function claimInput(input, { today, base = null } = {}) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src) return { ok: false, errors: { claim: 'Ugyldig utlegg.' } };
  const unknown = Object.keys(src).filter((k) => !FIELDS.includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const cur = base || { title: undefined, description: null, amountOre: undefined, spentOn: undefined, category: undefined, accountNo: undefined };
  const pick = (k) => (Object.prototype.hasOwnProperty.call(src, k) ? src[k] : cur[k]);
  const errors = {}, out = {};
  const take = (k, r) => { if (r.ok) out[k] = r.value; else errors[k] = r.message; };
  take('title', oneLine(pick('title'), 'hva utlegget gjelder', MAX_TITLE));
  take('description', text(pick('description'), 'beskrivelsen', MAX_DESCRIPTION));
  take('amountOre', amount(pick('amountOre')));
  const day = pick('spentOn');
  if (!Day.valid(day)) errors.spentOn = 'Datoen må være en gyldig dato (ÅÅÅÅ-MM-DD).';
  else if (today && Day.valid(today) && Day.diff(today, day) > 0) errors.spentOn = 'Datoen kan ikke være fram i tid.';
  else out.spentOn = day;
  const cat = pick('category');
  if (!CATEGORIES.includes(cat)) errors.category = 'Velg en kategori.'; else out.category = cat;
  take('accountNo', accountNumber(pick('accountNo')));
  if (Object.keys(errors).length) return { ok: false, errors };
  const changed = base ? FIELDS.filter((k) => (base[k] ?? null) !== (out[k] ?? null)) : FIELDS.filter((k) => out[k] != null);
  return { ok: true, value: out, changed };
}

/**
 * Whether a status change is allowed, and for whom ('claimant' | 'approver' | 'payer'). Same status is no transition.
 *   submitted → approved | rejected   the approver
 *   submitted → withdrawn             the claimant
 *   approved  → paid                  the payer
 */
function transition(from, to, who) {
  if (!STATUSES.includes(from) || !STATUSES.includes(to) || from === to) return false;
  if (from === 'submitted' && (to === 'approved' || to === 'rejected')) return who === 'approver';
  if (from === 'submitted' && to === 'withdrawn') return who === 'claimant';
  if (from === 'approved' && to === 'paid') return who === 'payer';
  return false;
}

/** A claim can be changed only while submitted, only by the one who sent it. */
const editable = (claim, actorId) => Boolean(claim && claim.status === 'submitted' && actorId && claim.claimantId === actorId && !viaLink(claim));
/**
 * A claim that came in through a receipt upload link (money-pro G3): its `claimantId` is the staff member who made the link
 * (the column is NOT NULL and points at staff), its `claimantName` the person who filled in the form. Nobody edits,
 * withdraws or confirms it as its claimant — the person outside has no session — and four eyes still holds: the one who
 * made the link never approves or pays what came through it.
 */
function viaLink(claim) { return Boolean(claim && claim.source === 'link'); }

const refuse = (code, message) => ({ ok: false, code, message });
/**
 * An approval or a rejection. Four eyes: the claimant never decides their own claim. A rejection says why; an approval
 * needs at least one receipt (the accountant's rule: no receipt, no refund).
 * → { ok, value: { status, decisionNote } } | { ok: false, code, message }
 */
function decision(claim, { to, actorId, note, receipts = 0 } = {}) {
  if (!claim || !isId(actorId)) return refuse('not_found', 'Fant ikke utlegget.');
  if (to !== 'approved' && to !== 'rejected') return refuse('invalid', 'Ugyldig avgjørelse.');
  if (claim.claimantId === actorId) return refuse('four_eyes', 'Du kan ikke godkjenne eller avvise ditt eget utlegg. En annen må gjøre det.');
  if (!transition(claim.status, to, 'approver')) return refuse('not_submitted', `Utlegget er «${STATUS_NO[claim.status] || claim.status}» og kan ikke avgjøres.`);
  const n = text(note, 'en begrunnelse', MAX_NOTE, to === 'rejected');
  if (!n.ok) return refuse('invalid', n.message);
  if (to === 'approved' && !(Number(receipts) > 0)) return refuse('no_receipts', 'Utlegget har ingen kvittering. Be den som sendte det om å legge ved kvitteringen før det godkjennes.');
  return { ok: true, value: { status: to, decisionNote: n.value } };
}
/** The claimant takes a submitted claim back. */
function withdrawal(claim, actorId) {
  if (!claim || !isId(actorId)) return refuse('not_found', 'Fant ikke utlegget.');
  if (claim.claimantId !== actorId || viaLink(claim)) return refuse('not_permitted', 'Bare den som sendte utlegget kan trekke det.');
  if (!transition(claim.status, 'withdrawn', 'claimant')) return refuse('not_submitted', `Utlegget er «${STATUS_NO[claim.status] || claim.status}» og kan ikke trekkes.`);
  return { ok: true, value: { status: 'withdrawn' } };
}
/**
 * The treasurer has transferred the money: approved → paid, with the bank's reference (optional, at most 80 characters).
 * Four eyes here too: the claimant never marks their own claim paid, since that is the step that moves the money.
 */
function payment(claim, { actorId, ref } = {}) {
  if (!claim || !isId(actorId)) return refuse('not_found', 'Fant ikke utlegget.');
  if (claim.claimantId === actorId) return refuse('four_eyes', 'Du kan ikke merke ditt eget utlegg betalt. En annen må gjøre det.');
  if (!transition(claim.status, 'paid', 'payer')) return refuse('not_approved', `Utlegget er «${STATUS_NO[claim.status] || claim.status}» og kan ikke merkes betalt.`);
  const r = oneLine(ref, 'en referanse', MAX_REF, false);
  if (!r.ok) return refuse('invalid', r.message);
  return { ok: true, value: { status: 'paid', paidRef: r.value } };
}
/** Receipts are added while the claim is submitted, and only so many. */
function receiptAllowed(claim, actorId, count) {
  if (!editable(claim, actorId)) return refuse('not_editable', 'Kvitteringer legges til mens utlegget venter på godkjenning, av den som sendte det.');
  if (count >= MAX_RECEIPTS) return refuse('too_many_receipts', `Et utlegg kan ha høyst ${MAX_RECEIPTS} kvitteringer.`);
  return { ok: true };
}

/** The approver's list filter: `open` (waiting or approved, the default), one status, or `all`. Unknown values are refused. */
const STATUS_FILTERS = ['open', 'all', ...STATUSES];
function listFilter(q = {}) {
  const status = q.status === undefined || q.status === null || q.status === '' ? 'open' : String(q.status);
  if (!STATUS_FILTERS.includes(status)) return { ok: false, message: 'Ugyldig statusfilter.' };
  return { ok: true, value: { status } };
}
const matches = (claim, f) => (f.status === 'all' ? true : f.status === 'open' ? OPEN.includes(claim.status) : claim.status === f.status);
/** Waiting first, then the newest change first; ties end on the id so two pages never swap a row. */
function sort(claims) {
  const rank = (c) => (c.status === 'submitted' ? 0 : c.status === 'approved' ? 1 : 2);
  return [...claims].sort((a, b) => rank(a) - rank(b) || String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')) || String(a.id).localeCompare(String(b.id)));
}

/** The export's period, from the query: both days, from ≤ to; the default is this year up to today (day.js). */
const period = Day.period;

/**
 * The accountant's file: one line per PAID claim — the day it was spent, the day it was paid, what, who, how much (kr
 * with a comma, no unit, no grouping), the category, the bank reference. Semicolons (Norwegian Excel), CRLF, quoted
 * when a value needs it. `paidOn` is the paid day mosque-local, computed by the caller.
 */
const CSV_HEAD = ['Dato', 'Betalt', 'Hva', 'Innsendt av', 'Beløp (kr)', 'Kategori', 'Referanse', 'Bekreftet av utlegger', 'Godkjenning bekreftet'];
// Typed text through Csv.cell (a formula start gets an apostrophe: a claim title is typed by whoever sent the claim, and
// the accountant opens the file); the amount is ours, through Csv.num.
function csvLine(c) {
  const t = Csv.cell;
  return Csv.line([t(c.spentOn), t(c.paidOn), t(c.title), t(c.claimantName), Csv.num(formatKr(c.amountOre, { grouped: false })),
    t(CATEGORY_NO[c.category] || c.category), t(c.paidRef || ''), t(c.claimantConfirmed || 'nei'), t(c.approvalConfirmed || 'nei')]);
}
function csv(claims) {
  return Csv.table(CSV_HEAD, claims.map(csvLine));
}

/**
 * Who may confirm a claim with Vipps (packages/core/vipps-signature.js, kind expense_claim), and when:
 *   claimant   the one who sent it, while it is submitted — «Bekreft med Vipps» after filling it in and adding receipts;
 *   approver   someone else (four eyes), while it is submitted, with at least one receipt, and only once the claimant's own
 *              confirmation is valid for the claim as it is now — «Godkjenn og bekreft med Vipps» approves it as well.
 * → { ok } | { ok: false, code, message }. Checked when the confirmation starts and again when it is stored.
 */
function confirmAllowed(claim, role, actorId, { receipts = 0, claimantValid = false } = {}) {
  if (!claim || !isId(actorId)) return refuse('not_found', 'Fant ikke utlegget.');
  if (claim.status !== 'submitted') return refuse('not_submitted', `Utlegget er «${STATUS_NO[claim.status] || claim.status}» og kan ikke bekreftes nå.`);
  if (role === 'claimant') {
    if (viaLink(claim)) return refuse('via_link', 'Utlegget kom inn via en opplastingslenke. Den som sendte det, kan ikke bekrefte med Vipps.');
    if (claim.claimantId !== actorId) return refuse('not_signer', 'Bare den som sendte utlegget kan bekrefte det som utlegger.');
    return { ok: true };
  }
  if (role === 'approver') {
    if (claim.claimantId === actorId) return refuse('four_eyes', 'Du kan ikke godkjenne ditt eget utlegg. En annen må gjøre det.');
    if (!(Number(receipts) > 0)) return refuse('no_receipts', 'Utlegget har ingen kvittering. Be den som sendte det om å legge ved kvitteringen før det godkjennes.');
    // A claim through an upload link has no claimant confirmation to wait for (nobody outside can sign in): the approver's
    // own confirmation is the only one, and the panel says «Uten Vipps-bekreftelse fra innsender».
    if (!claimantValid && !viaLink(claim)) return refuse('claimant_not_confirmed', 'Utlegget er ikke bekreftet med Vipps av den som sendte det (eller det er endret siden).');
    return { ok: true };
  }
  return refuse('invalid', 'Ukjent rolle.');
}

/**
 * The accountant's one-claim PDF (receipt-pdf.js: WinAnsi, one column): the claim's fields, the receipts, the decision
 * and payment, and the Vipps confirmations (the line from vipps-signature.stampLine, with «gjelder» or «gjelder ikke
 * lenger»). `stamps` = { claimant, approver } as views ({ line, valid, sealed }) or null. The account number masked.
 */
function claimPdfLines(c, { claimantName = '', decidedByName = '', paidByName = '', receipts = [], stamps = {}, contentRef = '', when = (x) => x || '' } = {}) {
  const stamp = (label, s) => (s ? [`${label}: ${s.line}${s.valid ? '' : ' (gjelder ikke lenger — utlegget er endret)'}${s.sealed ? ' · forseglet' : ''}`] : [`${label}: nei`]);
  return [
    `Utlegg: ${c.title}`,
    `Beløp: ${kr(c.amountOre)}`,
    `Dato: ${c.spentOn}`,
    `Kategori: ${CATEGORY_NO[c.category] || c.category}`,
    `Sendt av: ${claimantName || '—'}`,
    `Kontonummer: ${accountMasked(c.accountNo)}`,
    ...(c.description ? [`Beskrivelse: ${c.description}`] : []),
    `Status: ${STATUS_NO[c.status] || c.status}`,
    ...(c.decidedAt ? [`${c.status === 'rejected' ? 'Avvist' : 'Godkjent'} av: ${decidedByName || '—'} · ${when(c.decidedAt)}`] : []),
    ...(c.paidAt ? [`Betalt av: ${paidByName || '—'} · ${when(c.paidAt)}${c.paidRef ? ` · ref. ${c.paidRef}` : ''}`] : []),
    '',
    `Kvitteringer (${receipts.length}):`,
    ...receipts.map((r) => `- ${r.filename} (${r.contentType}, ${r.size} byte)`),
    '',
    'Bekreftelser med Vipps (identifisert med Vipps Login og bundet til innholdet over):',
    ...stamp('Bekreftet av utlegger', stamps.claimant),
    ...stamp('Godkjenning bekreftet', stamps.approver),
    `Innholdets referanse nå: ${contentRef}`,
  ];
}

/**
 * A receipt as uploaded: PDF, JPEG, PNG or HEIC decided by its first bytes (request-files.sniff — never by the name or the
 * type the phone claims), not empty, at most 8 MB; the name cleaned for display. → { ok, value: { mime, size, filename } }
 * | { ok: false, code, message }
 */
function receipt(bytes, filename) {
  const size = bytes ? bytes.length : 0;
  if (!size) return refuse('empty', 'Kvitteringen er tom.');
  if (size > MAX_RECEIPT_BYTES) return refuse('too_large', `Kvitteringen er for stor (maks ${MAX_RECEIPT_BYTES / 1024 / 1024} MB).`);
  const mime = RF.sniff(bytes);
  if (!mime) return refuse('unsupported_type', 'Kvitteringen må være et bilde (JPEG, PNG, HEIC) eller en PDF.');
  const ext = RF.TYPES[mime].ext;
  const name = RF.safeFilename(filename, `kvittering.${ext}`);
  return { ok: true, value: { mime, size, filename: name } };
}

/**
 * What the activity log keeps of a claim: what it was for, how much, the category, the day, its status, how many receipts
 * and the bank reference. NEVER the account number, the description or a decision note (staff's words about a person).
 * Every key starts with expense_ (core audit.js EXPENSE_FIELDS, expenseAudit()).
 */
function auditView(c = {}) {
  const out = {};
  if (c.title !== undefined) out.expense_title = c.title;
  if (c.amountOre !== undefined) out.expense_amount_ore = c.amountOre;
  if (c.category !== undefined) out.expense_category = c.category;
  if (c.spentOn !== undefined) out.expense_spent_on = c.spentOn;
  if (c.status !== undefined) out.expense_status = c.status;
  if (c.receipts !== undefined) out.expense_receipts = c.receipts;
  if (c.paidRef !== undefined && c.paidRef !== null) out.expense_paid_ref = c.paidRef;
  return out;
}

/* ------------------------------------------------------------------ the month view (money-pro G2, 2026-10-31) */

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const validMonth = (m) => typeof m === 'string' && MONTH_RE.test(m);
/** The `months` months ending with today's (mosque-local), oldest first: ['2025-11', …, '2026-10']. */
function monthsBack(today, months = 12) {
  if (!Day.valid(today)) return [];
  const n = Math.max(1, Math.min(36, Number.isSafeInteger(months) ? months : 12));
  const y = Number(today.slice(0, 4)), m = Number(today.slice(5, 7));
  const out = [];
  for (let k = n - 1; k >= 0; k -= 1) { const a = Day.addMonths(y, m, -k); out.push(`${a.y}-${String(a.m).padStart(2, '0')}`); }
  return out;
}
/** Approved and paid count as spent; submitted is what still waits; rejected and withdrawn count for nothing. */
const SPENT = ['approved', 'paid'];
const tally = (row, c) => {
  if (SPENT.includes(c.status)) { row.approvedOre += c.amountOre; row.count += 1; }
  else if (c.status === 'submitted') { row.submittedOre += c.amountOre; row.count += 1; }
};
/**
 * «Utlegg per måned»: for each of the last `months` months — by the day the money was spent, a wall date already (the
 * claimant's `spentOn`, checked against the mosque's day) — the approved+paid sum and the submitted-not-yet-decided sum,
 * in øre, and how many claims. Pure: `today` is the mosque's day (tz.dateIn), passed in.
 * → [{ month, approvedOre, submittedOre, count }], oldest first.
 */
function byMonth(claims, { today, months = 12 } = {}) {
  const keys = monthsBack(today, months);
  const at = new Map(keys.map((k) => [k, { month: k, approvedOre: 0, submittedOre: 0, count: 0 }]));
  for (const c of claims || []) {
    const row = c && at.get(String(c.spentOn || '').slice(0, 7));
    if (row && Number.isSafeInteger(c.amountOre)) tally(row, c);
  }
  return keys.map((k) => at.get(k));
}
/**
 * One month's totals per category, every category listed (zero included) in the catalogue's order.
 * → { month, approvedOre, submittedOre, categories: [{ category, label_no, approvedOre, submittedOre, count }] }
 */
function monthTotals(claims, month) {
  const cats = CATEGORIES.map((k) => ({ category: k, label_no: CATEGORY_NO[k], approvedOre: 0, submittedOre: 0, count: 0 }));
  const of = new Map(cats.map((c) => [c.category, c]));
  for (const c of claims || []) {
    if (!c || String(c.spentOn || '').slice(0, 7) !== month || !Number.isSafeInteger(c.amountOre)) continue;
    const row = of.get(c.category);
    if (row) tally(row, c);
  }
  return { month, approvedOre: cats.reduce((n, c) => n + c.approvedOre, 0), submittedOre: cats.reduce((n, c) => n + c.submittedOre, 0), categories: cats };
}
/** The summary's query: `months` 1–36 (default 12), `month` YYYY-MM (default today's). → { ok, value: { months, month } } */
function summaryQuery(q = {}, today) {
  const months = q.months === undefined || q.months === null || q.months === '' ? 12 : Number(q.months);
  if (!Number.isSafeInteger(months) || months < 1 || months > 36) return { ok: false, message: 'Antall måneder må være mellom 1 og 36.' };
  const month = q.month === undefined || q.month === null || q.month === '' ? String(today || '').slice(0, 7) : String(q.month);
  if (!validMonth(month)) return { ok: false, message: 'Velg en gyldig måned (ÅÅÅÅ-MM).' };
  return { ok: true, value: { months, month } };
}

/* ------------------------------------------------------------------ receipt upload links (money-pro G3, 2026-10-31) */

const LINK_TOKEN_BYTES = 32;
const LINK_TOKEN_SHAPE = /^[A-Za-z0-9_-]{43}$/;
const linkTokenShape = (token) => typeof token === 'string' && LINK_TOKEN_SHAPE.test(token);
const MAX_LINK_LABEL = 80;
const MAX_LINK_DAYS = 30;
const DEFAULT_LINK_DAYS = 7;
const MAX_LINK_USES = 10;
const MAX_CLAIMANT_NAME = 120;
/** At most this many links listed (newest first); a mosque never needs more open at once. */
const MAX_LINKS_LISTED = 200;
const intIn = (v, lo, hi, dflt) => {
  if (v === undefined || v === null || v === '') return { ok: true, value: dflt };
  const n = typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v.trim()) : v;
  return Number.isSafeInteger(n) && n >= lo && n <= hi ? { ok: true, value: n } : { ok: false };
};
/**
 * A new upload link as staff make it: who it is for (≤ 80, shown in the list and on the claims it brings), how many days
 * it works (1–30, default 7) and how many claims it may bring (1–10, default 1). Unknown fields refused.
 * → { ok, value: { label, days, maxUses, expiresAt } } | { ok: false, errors }
 */
function linkInput(input, { now = new Date() } = {}) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src) return { ok: false, errors: { link: 'Ugyldig lenke.' } };
  const unknown = Object.keys(src).filter((k) => !['label', 'days', 'maxUses'].includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const errors = {};
  const label = oneLine(src.label, 'hvem lenken er til', MAX_LINK_LABEL);
  if (!label.ok) errors.label = label.message;
  const days = intIn(src.days, 1, MAX_LINK_DAYS, DEFAULT_LINK_DAYS);
  if (!days.ok) errors.days = `Lenken kan virke fra 1 til ${MAX_LINK_DAYS} dager.`;
  const uses = intIn(src.maxUses, 1, MAX_LINK_USES, 1);
  if (!uses.ok) errors.maxUses = `Lenken kan brukes fra 1 til ${MAX_LINK_USES} ganger.`;
  if (Object.keys(errors).length) return { ok: false, errors };
  const expiresAt = new Date(new Date(now).getTime() + days.value * 86400000).toISOString();
  return { ok: true, value: { label: label.value, days: days.value, maxUses: uses.value, expiresAt } };
}
/** open | used | expired | revoked — a row as stored (snake_case). Revoked wins, then used up, then the clock. */
function linkState(row, now = new Date()) {
  if (!row) return 'expired';
  if (row.revoked_at) return 'revoked';
  if (Number(row.used_count) >= Number(row.max_uses)) return 'used';
  return new Date(row.expires_at) > new Date(now) ? 'open' : 'expired';
}
const linkOpen = (row, now) => linkState(row, now) === 'open';
const LINK_STATE_NO = { open: 'Åpen', used: 'Brukt', expired: 'Utløpt', revoked: 'Trukket tilbake' };
function linkView(row, { now = new Date(), nameOf = () => null } = {}) {
  return { id: row.id, label: row.label, state: linkState(row, now), createdAt: row.created_at, createdByName: nameOf(row.created_by),
    expiresAt: row.expires_at, usedCount: Number(row.used_count) || 0, maxUses: Number(row.max_uses) || 1, revokedAt: row.revoked_at || null };
}

const PUBLIC_FIELDS = ['name', 'title', 'description', 'amountOre', 'spentOn', 'category', 'accountNo'];
/**
 * A claim as a person outside the panel sends it through an upload link: their name, then what a claimant writes (core
 * claimInput: title, amount, day, category, description, the bank account the money goes to). Unknown fields refused.
 * → { ok, value: { claimantName, title, description, amountOre, spentOn, category, accountNo } } | { ok: false, errors }
 */
function publicClaimInput(input, { today } = {}) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : null;
  if (!src) return { ok: false, errors: { claim: 'Ugyldig utlegg.' } };
  const unknown = Object.keys(src).filter((k) => !PUBLIC_FIELDS.includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const { name, ...rest } = src;
  const n = oneLine(name, 'navnet ditt', MAX_CLAIMANT_NAME);
  const c = claimInput(rest, { today });
  const errors = { ...(n.ok ? {} : { name: n.message }), ...(c.ok ? {} : c.errors) };
  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, value: { claimantName: n.value, ...c.value } };
}

/* ------------------------------------------------------------------ AI receipt prefill (money-pro G4, 2026-10-31) */

/** What the AI reader may be sent: the pictures it reads, and PDFs. A HEIC photo is stored as it is, but not read. */
const READABLE = ['image/jpeg', 'image/png', 'application/pdf'];
/** The reader's guess at a category, in its words → ours. Anything else is no guess. */
const CATEGORY_GUESS = { food: 'food', groceries: 'food', restaurant: 'food', equipment: 'equipment', hardware: 'equipment', electronics: 'equipment',
  travel: 'travel', transport: 'travel', fuel: 'travel', event: 'event', maintenance: 'maintenance', repair: 'maintenance', cleaning: 'maintenance', other: 'other' };
/**
 * The reader's answer → fields to PREFILL the form, each only when it is a value the form would accept: an amount in NOK
 * (whole øre, within the claim limit), a day not after today, the vendor as the title (≤ 120), a known category; and the
 * confidence 0–1. Nothing here is saved — the person checks and sends. A foreign currency gives no amount (a claim is in
 * kroner); its code is passed on so the form can say so.
 * → { ok: true, value: { amountOre?, spentOn?, title?, category?, currency, confidence } } | { ok: false }
 */
function receiptPrefill(answer, { today } = {}) {
  if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return { ok: false };
  const out = {};
  const currency = typeof answer.currency === 'string' && /^[A-Za-z]{3}$/.test(answer.currency.trim()) ? answer.currency.trim().toUpperCase() : null;
  out.currency = currency;
  if ((currency === null || currency === 'NOK') && amount(answer.amount_ore).ok) out.amountOre = answer.amount_ore;
  if (Day.valid(answer.date) && (!Day.valid(today) || Day.diff(today, answer.date) <= 0)) out.spentOn = answer.date;
  const v = oneLine(typeof answer.vendor === 'string' ? answer.vendor.slice(0, MAX_TITLE) : null, 'butikken', MAX_TITLE, false);
  if (v.ok && v.value) out.title = v.value;
  const g = typeof answer.category_guess === 'string' ? CATEGORY_GUESS[answer.category_guess.trim().toLowerCase()] : null;
  if (g) out.category = g;
  const conf = Number(answer.confidence);
  out.confidence = Number.isFinite(conf) ? Math.max(0, Math.min(1, Math.round(conf * 100) / 100)) : 0;
  return { ok: true, value: out };
}

module.exports = { STATUSES, OPEN, FINAL, CATEGORIES, STATUS_NO, CATEGORY_NO, STATUS_FILTERS, FIELDS,
  MAX_TITLE, MAX_DESCRIPTION, MAX_NOTE, MAX_REF, MAX_AMOUNT_ORE, MAX_RECEIPTS, MAX_RECEIPT_BYTES, MAX_CLAIMS, CSV_HEAD,
  isId, accountNumber, accountText, accountMasked, kroner, amount, formatKr, kr, claimInput, transition, editable,
  decision, withdrawal, payment, receiptAllowed, receipt, auditView, confirmAllowed, claimPdfLines, listFilter, matches, sort, period, csvLine, csv,
  viaLink, validMonth, monthsBack, byMonth, monthTotals, summaryQuery,
  LINK_TOKEN_BYTES, linkTokenShape, MAX_LINK_LABEL, MAX_LINK_DAYS, DEFAULT_LINK_DAYS, MAX_LINK_USES, MAX_CLAIMANT_NAME, MAX_LINKS_LISTED, LINK_STATE_NO,
  linkInput, linkState, linkOpen, linkView, PUBLIC_FIELDS, publicClaimInput, READABLE, receiptPrefill };
